import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { AmcAnalysisTemplate } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import { IAlertComponent, IDatepickerComponent, IDateRange, IFormFieldComponent, ILabelComponent } from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { DateTime } from "luxon";

export interface AmcAnalysisPeriodModel {
  period: IDateRange | null;
}

export interface AmcAnalysisPeriodWindow {
  min: DateTime;
  max: DateTime;
}

@Component({
  selector: "app-amc-analysis-period-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, IAlertComponent, IDatepickerComponent, IFormFieldComponent, ILabelComponent],
  template: `<div class="flex flex-col gap-6 py-8" *transloco="let t">
    <div class="flex flex-col gap-1">
      <h4>{{ t("common.period") }}</h4>
      <p class="text-muted-foreground m-0">{{ t("amc.strategy_analysis_period_description") }}</p>
    </div>
    <IFormField>
      <label iLabel>{{ t("common.period") }}</label>
      <IDatePicker
        [dateFormat]="dateFormat()"
        [minDate]="window().min"
        [maxDate]="window().max"
        [withShortcuts]="false"
        placement="bottom"
        [selectedDateRange]="period().period().value()"
        (selectedDateRangeChange)="select($event)"
      />
    </IFormField>
    @if (incompleteResultTemplates().length) {
      <IAlert type="warning" [title]="t('amc.strategy_analysis_period_incomplete')">
        <ul class="m-0 list-disc ps-5">
          @for (template of incompleteResultTemplates(); track template) {
            <li>{{ t("amc-analysis-template." + template) }}</li>
          }
        </ul>
      </IAlert>
    }
  </div>`,
})
export class AmcAnalysisPeriodStepComponent {
  private readonly authService = inject(AuthService);

  readonly period = input.required<FieldTree<AmcAnalysisPeriodModel>>();
  readonly window = input.required<AmcAnalysisPeriodWindow>();
  readonly incompleteResultTemplates = input<AmcAnalysisTemplate[]>([]);

  private readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.locale()));

  select(range: IDateRange | null) {
    this.period().period().markAsDirty();
    this.period().period().value.set(range);
  }
}
